import type { SupportSession } from '@gat-ship/types'

/** Calendar/clock parts of a remaining-time duration (Temporal.Duration-shaped). */
export interface CountdownParts {
  years: number
  months: number
  days: number
  hours: number
  minutes: number
  seconds: number
}

/** The countdown has fully run out (vs. still ticking on a sub-field). */
export function isCountdownDone(parts: CountdownParts) {
  const { years, months, days, hours, minutes, seconds } = parts
  return (
    years === 0 &&
    months === 0 &&
    days === 0 &&
    hours === 0 &&
    minutes === 0 &&
    seconds === 0
  )
}

/**
 * Render the largest non-zero unit pair of a remaining-time duration,
 * or 'Ended' once nothing is left.
 */
export function formatTimeRemaining(parts: CountdownParts) {
  const { years, months, days, hours, minutes, seconds } = parts
  if (years > 0) {
    return `${years} years`
  }
  if (months > 0) {
    return `${months} months`
  }
  if (days > 0) {
    return `${days} days`
  }
  if (hours > 0) {
    return `${hours} h ${minutes} min`
  }
  if (minutes > 0) {
    return `${minutes} min ${seconds} sec`
  }
  if (seconds > 0) {
    return `${seconds} sec`
  }
  return 'Ended'
}

export function isSupportSessionExpired(supportSession: SupportSession) {
  const now = new Date()
  const endDate = new Date(supportSession.grantedTo)
  return endDate < now
}

/** Count of sessions that have not expired yet. */
export function countActiveSupportSessions(
  sessions: readonly SupportSession[]
): number {
  return sessions.filter((session) => !isSupportSessionExpired(session)).length
}

/** A fresh copy of the sessions with the given `sessionId` removed (input untouched). */
export function removeSupportSessionById(
  sessions: readonly SupportSession[],
  sessionId: string
): SupportSession[] {
  return sessions.filter((session) => session.sessionId !== sessionId)
}

/**
 * Fetch only on first load (empty list), unless the caller forces a refetch.
 * Pure guard for the data source's lazy fetch decision.
 */
export function shouldFetchSupportSessions(
  dataLength: number,
  forceFetch: boolean
): boolean {
  return dataLength === 0 || forceFetch
}

function compareByGrantedTo(a: SupportSession, b: SupportSession) {
  return new Date(a.grantedTo).getTime() - new Date(b.grantedTo).getTime()
}

/** A fresh copy of the sessions sorted by `grantedTo` ascending (input untouched). */
export function sortSupportSessionsByGrantedToAsc(
  sessions: readonly SupportSession[]
): SupportSession[] {
  return [...sessions].sort(compareByGrantedTo)
}

/** A fresh copy of the sessions sorted by `grantedTo` descending (input untouched). */
export function sortSupportSessionsByGrantedToDesc(
  sessions: readonly SupportSession[]
): SupportSession[] {
  return [...sessions].sort((a, b) => compareByGrantedTo(b, a))
}

/** Escalating attention level as a session's countdown approaches `grantedTo`. */
export type CountdownWarningLevel = 'normal' | 'warning' | 'critical'

/**
 * Countdown is spent once every part is at or below zero. Distinct from
 * `isCountdownDone` (all parts exactly zero): this also treats negative
 * over-run parts as expired.
 */
export function isCountdownExpired(parts: CountdownParts | undefined): boolean {
  if (!parts) return false
  return (
    parts.years <= 0 &&
    parts.months <= 0 &&
    parts.days <= 0 &&
    parts.hours <= 0 &&
    parts.minutes <= 0 &&
    parts.seconds <= 0
  )
}

/**
 * Warning level for a remaining-time countdown: 'critical' once expired or
 * within 5 minutes, 'warning' within 20 minutes, 'normal' otherwise (including
 * while whole days/months/years still remain).
 */
export function supportSessionWarningLevel(
  parts: CountdownParts | undefined,
  isExpired: boolean
): CountdownWarningLevel {
  if (!parts) return 'normal'
  if (isExpired) return 'critical'
  if (parts.years > 0 || parts.months > 0 || parts.days > 0) return 'normal'
  const minutes = parts.hours * 60 + parts.minutes
  if (minutes <= 5) return 'critical'
  if (minutes <= 20) return 'warning'
  return 'normal'
}
/**
 * Checks if the support session is assigned to the given support user.
 * @param supportPersonUserId - The session's assigned support person.
 * @param userId - The ID of the user to check against.
 */
export function isSupportSessionAssignedToSupportUser(
  supportPersonUserId: string,
  userId: string
) {
  return supportPersonUserId === userId
}
